/**
 * ServiceDashboard — tabbed section with Available / Ongoing / Completed.
 *
 * Displays badge counts on Ongoing and Completed tabs.
 * Switches between ServiceCatalog and ServiceTable based on active tab,
 * with smooth content transitions powered by tailwind keyframes.
 */
import * as React from 'react';
import { List, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { AnimatedTabs, type AnimatedTabItem } from '@finmin/ui';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { isFinished, isOngoing } from '../constants';
import { ServiceCatalog } from './service-catalog';
import { ServiceTable }   from './service-table';

const AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'available' | 'ongoing' | 'completed';

interface Props {
  requests: CustomerServiceRequestResponse[];
  isLoading: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ServiceDashboard({ requests, isLoading }: Props) {
  const [activeTab, setActiveTab] = React.useState<Tab>('available');

  const ongoingRequests   = requests.filter(isOngoing);
  const completedRequests = requests.filter(isFinished);

  const tabs: AnimatedTabItem<Tab>[] = [
    { value: 'available', label: 'Available', icon: Sparkles },
    {
      value: 'ongoing',
      label: 'Ongoing',
      icon:  Loader2,
      badge: ongoingRequests.length > 0 ? ongoingRequests.length : undefined,
    },
    {
      value: 'completed',
      label: 'Completed',
      icon:  CheckCircle2,
      badge: completedRequests.length > 0 ? completedRequests.length : undefined,
    },
  ];

  return (
    <section className="space-y-3">
      {/* Section heading */}
      <div className="flex items-center gap-2">
        <List className="h-5 w-5 shrink-0" style={{ color: AMBER }} aria-hidden />
        <h2 className="font-display font-semibold text-lg text-foreground tracking-tight">
          Service Dashboard
        </h2>
      </div>

      {/* Tab bar with animated underline */}
      <AnimatedTabs
        items={tabs}
        value={activeTab}
        onChange={setActiveTab}
        accent="forest"
        ariaLabel="Service dashboard tabs"
      />

      {/* Tab content — keyed so each switch re-mounts and replays the
          slide-up-fade animation. */}
      <div role="tabpanel" key={activeTab} className="animate-slide-up-fade">
        {activeTab === 'available' && (
          <ServiceCatalog />
        )}

        {activeTab === 'ongoing' && (
          <ServiceTable
            requests={ongoingRequests}
            isLoading={isLoading}
            emptyMessage="No ongoing services yet"
          />
        )}

        {activeTab === 'completed' && (
          <ServiceTable
            requests={completedRequests}
            isLoading={isLoading}
            emptyMessage="No completed services yet"
          />
        )}
      </div>
    </section>
  );
}
